import React from 'react';
import { motion } from 'framer-motion';

interface Service {
  id: number;
  title: string;
  description: string;
  features: string[];
  popular?: boolean;
}

const Services: React.FC = () => {
  const services: Service[] = [
    {
      id: 1,
      title: "AI Consulting",
      description: "Fine-tune your systems with expert guidance – without the overwhelm.",
      features: [
        "System audit & strategy session",
        "Personalized 1:1 session",
        "Visual workflows mapped in Miro",
        "AI, automation & toolstack guidance",
        "Loom reviews & feedback",
        "Templates Library"
      ]
    },
    {
      id: 2,
      title: "AI Development & Implementation",
      description: "For growing businesses needing more features and flexibility.",
      features: [
        "Custom-built AI solutions",
        "AI Agents development",
        "Automations via Make/Zapier",
        "Internal wiki & knowledge base",
        "Cross-tool integrations",
        "Team training & handover"
      ],
      popular: true
    }
  ];

  return (
    <div className="gradient-bg py-24">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
            >
              {service.popular && (
                <div className="absolute -top-4 right-8 bg-accent px-4 py-1 rounded-full">
                  <span className="text-sm font-medium">Most popular</span>
                </div>
              )}
              
              <div className="bg-black/30 backdrop-blur-sm rounded-xl p-8">
                <h3 className="text-2xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-300 mb-6">{service.description}</p>
                
                <h4 className="text-lg font-medium mb-4">What's Included:</h4>
                <ul className="space-y-3">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="mt-8">
                  <a 
                    href="#contact" 
                    className={`inline-flex items-center px-6 py-3 rounded-md font-medium transition-all duration-300 ${
                      service.popular 
                        ? 'bg-accent hover:bg-accent-light text-white' 
                        : 'border border-white/20 hover:border-white/40 text-white'
                    }`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                    Learn More
                  </a>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Services;
